import { nanoid } from 'nanoid';
import { toTitleCase } from 'libs/utils/displayUtils';

export enum OnboardEnum {
  child = 'merchant',
  parent = 'parent',
  grandparent = 'grandparent',
}

export enum PreferredDisplayMode {
  IN_CONTEXT = 'IN_CONTEXT',
  REDIRECT = 'REDIRECT',
}

export type FormField = { elemId: keyof OnboardingForm, displayName: string, optional?: boolean};

export class OnboardingForm {
  public parentId: string;

  public redirectUrl: string;

  public name: string;

  public companyNumber: string;

  public financeEmail: string;

  public countryOfCompany: string | null;

  public themeCustomisationEnabled: boolean;

  public leftSidebarBgColour: string;

  public logoFile: File | null;

  // Fallback/inherited logo URL — defaults to the standard Optty logo, or the
  // selected parent/grandparent's logo when one is chosen. Forwarded as
  // `theme.logo` alongside logoFile so the entity always has a usable logo
  // even when the user doesn't upload a new one.
  public logoUrl: string;

  public isAllOptIn: boolean;

  public leftSidebarActiveColour: string;

  public leftSidebarHoverColour: string;

  public leftSidebarTextColour: string;

  public topBarTextColour: string;

  public topBarBgColour: string;

  public hierarchyLevel: string;

  public userFirstName: string;

  public userLastName: string;

  public userEmail: string;

  public userPassword: string;

  public userConfirmPassword: string;

  public userSubRoles: string[];

  // Merchant-only fields, matching upp-portal's Merchant onboarding
  public partnerMerchantId: string;

  public preferredDisplayMode: PreferredDisplayMode;

  public paymentLinkMenuAlias: string;

  public popupTitle: string;

  public popupMessage: string;

  constructor(heirachyLevel: OnboardEnum) {
    this.name = '';
    this.parentId = '';
    this.companyNumber = '';
    this.countryOfCompany = '';
    this.logoFile = null;
    this.logoUrl = '';
    this.isAllOptIn = true;
    this.leftSidebarBgColour = '';
    this.leftSidebarActiveColour = '';
    this.leftSidebarHoverColour = '';
    this.leftSidebarTextColour = '';
    this.topBarTextColour = '';
    this.topBarBgColour = '';
    this.hierarchyLevel = heirachyLevel;
    this.financeEmail = '';
    this.redirectUrl = '';
    this.themeCustomisationEnabled = false;

    this.userFirstName = '';
    this.userLastName = '';
    this.userEmail = '';
    this.userPassword = '';
    this.userConfirmPassword = '';
    this.userSubRoles = [];

    // Matches upp-portal's AddNewMerchantPopUp, which generates one as soon as
    // the create-merchant modal opens rather than requiring a manual click.
    this.partnerMerchantId = heirachyLevel === OnboardEnum.child ? nanoid(16) : '';
    this.preferredDisplayMode = PreferredDisplayMode.REDIRECT;
    this.paymentLinkMenuAlias = '';
    this.popupTitle = '';
    this.popupMessage = '';
  }

  public static errorMessage(form: OnboardingForm, level: OnboardEnum): string | null {
    if (!form.name.trim()) {
      return `${toTitleCase(level)} Name is required`;
    }

    // Basic company details - always required
    if (!form.companyNumber.trim()) {
      return 'Company Number is required.';
    }
    if (!form.financeEmail || !OnboardingForm.isValidEmail(form.financeEmail)) {
      return 'A valid Finance Email is required';
    }

    // Additional validation for top-level entities (those without a parent)
    if (!form.parentId) {
      if (!form.countryOfCompany) {
        return 'A valid two-letter Country Code is required';
      }
    }

    // User details - always required
    if (!form.userFirstName.trim()) {
      return 'User First Name is required';
    }
    if (!form.userLastName.trim()) {
      return 'User Last Name is required';
    }
    if (!form.userEmail || !OnboardingForm.isValidEmail(form.userEmail)) {
      return 'A valid User Email is required';
    }
    if (!form.userPassword || form.userPassword.length < 8) {
      return 'A User Password of at least 8 characters is required';
    }
    if (form.userPassword !== form.userConfirmPassword) {
      return 'User Password and Confirm Password must match';
    }

    // Merchant-only validation
    if (form.hierarchyLevel === OnboardEnum.child) {
      if (!form.redirectUrl || !OnboardingForm.isValidURL(form.redirectUrl)) {
        return 'A valid Redirect URL is required (Include the https://...)';
      }
      if (form.partnerMerchantId && !OnboardingForm.isValidPartnerMerchantId(form.partnerMerchantId)) {
        return 'Partner Merchant Reference ID must be 1-32 characters (letters, numbers, "_" or "-")';
      }
    }

    return null;
  }

  // Unlike the rest of errorMessage()'s checks, a password mismatch can't be
  // "confirmed and proceeded with" — the submission would just get rejected
  // by the backend. Checked separately so it always hard-blocks submission.
  public static hasPasswordMismatch(form: OnboardingForm): boolean {
    return !!form.userPassword && form.userPassword !== form.userConfirmPassword;
  }

  public static isValidEmail(email: string): boolean {
    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    return emailRegex.test(email);
  }

  public static isValidPartnerMerchantId(value: string): boolean {
    return /^[a-zA-Z0-9_-]{1,32}$/.test(value);
  }

  private static isValidURL(url: string): boolean {
    const urlRegex = /[^\s/$.?#].[^\s]*$/;
    return urlRegex.test(url);
  }

  public static InvoiceTextFields: FormField[] = [
    { elemId: 'financeEmail', displayName: 'Finance Email' },
    { elemId: 'companyNumber', displayName: 'Company Number (e.g. ABN)' },
    { elemId: 'countryOfCompany', displayName: 'Country of Company' },
    { elemId: 'redirectUrl', displayName: 'Redirect URL' },
  ];

  public static UserTextFields: FormField[] = [
    { elemId: 'userFirstName', displayName: 'User First Name' },
    { elemId: 'userLastName', displayName: 'User Last Name' },
    { elemId: 'userEmail', displayName: 'User Email' },
  ];
}
